import playbookJson from '../data/playbook.json';
import rulesJson from '../data/rules.json';
import {
  normalizeFranchisePlayer,
  FRANCHISE_PLAYER_POSITIONS,
  type FranchisePlayer,
} from './player-model';
import {
  type FranchiseDepthChartConfig,
} from './depth-chart-model';
import {
  canonicalPlayerId,
  canonicalTeamId,
  getFranchiseId,
} from './team-aliases';
import { applySeasonThreeFranchisePreset } from './season-three';

export * from './player-model';
export * from './player-play-style';
export * from './player-career';
export * from './depth-chart-model';
export {
  centralDateParts,
  localDateString,
  isPlayoffRound,
  PLAYOFF_ROUNDS,
} from '../types/scheduling-types';
export type {
  BroadcastTiming,
  ScheduledGameStatus,
  SeasonGame,
  PlayoffRound,
  LiveCoachingActivation,
  LiveCoachingPresetCommand,
  LiveCoachingState,
  LiveCoachingTeamState,
} from '../types/scheduling-types';
export * from './season-results';
export * from './schedule-builder';
export * from './canonicalize';
export * from './schedulingApi';
export * from './adminApi';
export * from './beta-access';
export * from './beta-season-two';
export * from './season-three';
export * from './season-four';
export * from './team-aliases';
export * from './nft-player-metadata';
export * from './error-reporting';
export * from '../helpers/colorHelper'

export type TeamColors = {
  primary: string;
  secondary: string;
  accent: string;
};

export const FRANCHISE_OFFENSE_SCHEMES = [
  'balanced',
  'west-coast',
  'spread',
  'option',
  'power',
  'vertical',
] as const;

export const FRANCHISE_DEFENSE_SCHEMES = [
  'front',
  'shell',
  'pressure',
  'match',
] as const;

export type FranchiseOffenseScheme = typeof FRANCHISE_OFFENSE_SCHEMES[number];
export type FranchiseDefenseScheme = typeof FRANCHISE_DEFENSE_SCHEMES[number];

const LEGACY_OFFENSE_SCHEME_ALIASES = {
  ground: 'power',
} as const satisfies Record<string, FranchiseOffenseScheme>;

const LEGACY_DEFENSE_SCHEME_ALIASES = {
  coverage: 'shell',
  swarm: 'pressure',
} as const satisfies Record<string, FranchiseDefenseScheme>;

export const FRANCHISE_OFFENSE_SCHEME_LABELS: Record<FranchiseOffenseScheme, string> = {
  balanced: 'Balanced',
  'west-coast': 'West Coast',
  spread: 'Spread',
  option: 'Option',
  power: 'Power',
  vertical: 'Vertical',
};

export const FRANCHISE_DEFENSE_SCHEME_LABELS: Record<FranchiseDefenseScheme, string> = {
  front: 'Front',
  shell: 'Shell',
  pressure: 'Pressure',
  match: 'Match',
};

export type FranchiseSchemeProfile = {
  identity: string;
  summary: string;
  strengths: string[];
  weaknesses: string[];
  bestWith: string;
  helpsAgainst: string;
  vulnerableTo: string;
  visuals: string;
};

export const FRANCHISE_OFFENSE_SCHEME_PROFILES: Record<FranchiseOffenseScheme, FranchiseSchemeProfile> = {
  balanced: {
    identity: 'Multiple and adaptable',
    summary: 'Keeps the call sheet wide open so the offense can move between quick game, play action, and steady run balance without telegraphing its plan.',
    strengths: ['Pivots cleanly when `front` or `pressure` defenses sell out to one answer', 'Can shift between quick game, play-action, and steady runs without changing personality'],
    weaknesses: ['Does not naturally stress `shell` looks unless the roster can win vertically', 'Feels less overwhelming if you want one clear signature attack'],
    bestWith: 'Balanced QB play, reliable line play, and no glaring skill-position holes.',
    helpsAgainst: 'One-dimensional defenses or opponents that overplay one phase.',
    vulnerableTo: 'Elite units that can win without needing to guess right.',
    visuals: 'Mixed personnel, varied route depth, and a blend of compact and spread spacing.',
  },
  'west-coast': {
    identity: 'Rhythm and efficiency',
    summary: 'Lives on timing, spacing, and ball distribution, asking the QB to stay on schedule and let skill players stack yards after the catch.',
    strengths: ['Quick game gives it built-in answers against `pressure` looks', 'Can stay efficient into lighter `shell` structures by living underneath'],
    weaknesses: ['`Match` coverage can crowd the intermediate windows it wants', 'Explosive plays depend on precision more than pure chunk-shot volume'],
    bestWith: 'Accurate QB, dependable slot targets, and smart RB or TE checkdown options.',
    helpsAgainst: 'Pressure-heavy defenses and coverage units that concede underneath grass.',
    vulnerableTo: 'Fast rally defenses that tackle cleanly and squeeze windows.',
    visuals: 'Compact splits, quick throws, crossers, stick routes, and chain-moving timing concepts.',
  },
  spread: {
    identity: 'Space and leverage',
    summary: 'Stretches the field horizontally, widens defenders, and creates room for quick throws, screens, options, and perimeter stress.',
    strengths: ['Widens `front` boxes and punishes over-aggressive `pressure` looks with space calls', 'Creates easy touches for speed against slower second-level defenders'],
    weaknesses: ['Disciplined `match` structures can squeeze the easy access throws', 'Tight red-zone or short-yardage football can feel less physical'],
    bestWith: 'Mobile or decisive QB play and skill players with burst in space.',
    helpsAgainst: 'Loaded boxes, aggressive fronts, and slower second-level defenders.',
    vulnerableTo: 'Disciplined coverage teams that tackle in space and stay on top of motions.',
    visuals: 'Wide splits, lighter boxes, screens, quick game, and horizontal pressure before vertical punches.',
  },
  option: {
    identity: 'Mobile QB stress',
    summary: 'Builds the offense around read-option looks, pistol spacing, QB keepers, and perimeter misdirection that make the defense account for the quarterback as a runner.',
    strengths: ['Creates designed rushing volume for mobile quarterbacks', 'Punishes aggressive `front` and `pressure` looks that overcommit to the backfield'],
    weaknesses: ['Can stall if the quarterback is not explosive enough to threaten the edge', 'Obvious passing situations still ask the QB to win from the pocket'],
    bestWith: 'Mobile QB, athletic line play, and backs or slots who can sell mesh-point conflict.',
    helpsAgainst: 'Aggressive fronts, slower linebackers, and defenses that chase motion too hard.',
    vulnerableTo: 'Disciplined `match` structures and physical edges that force the ball back inside.',
    visuals: 'Pistol and gun looks, zone read, speed option, QB draw, boot keepers, and misdirection around the mesh point.',
  },
  power: {
    identity: 'Downhill control',
    summary: 'Builds the offense around physical runs, condensed formations, and play-action counters that force defenses to survive a long, bruising night.',
    strengths: ['Leans on lighter `shell` boxes and makes run fits pay all night', 'Play-action gets sharper once defenses start crowding the run'],
    weaknesses: ['`Front` structures and overloaded boxes can bog it down early', 'Falls behind faster if early-down runs never get moving'],
    bestWith: 'Strong line play, a sturdy backfield, and tight ends or fullbacks who can support the edge.',
    helpsAgainst: 'Light boxes, passive shells, and finesse fronts.',
    vulnerableTo: 'Penetrating fronts that win early and force long-yardage scripts.',
    visuals: 'Condensed sets, downhill runs, boots, and a more physical point of attack.',
  },
  vertical: {
    identity: 'Explosive and high-variance',
    summary: 'Pushes the ball downfield with deeper drops, layered shots, and route stems designed to stress safeties and corners at full depth.',
    strengths: ['Most dangerous into single-high looks from `front` or aggressive defenses', 'Can flip drives fast when the QB and WRs win the deep ball'],
    weaknesses: ['`Shell` and `match` take away cleaner deep windows', '`Pressure` can speed up the pocket before the routes mature'],
    bestWith: 'Strong-arm QB, outside speed, and pass protection that can hold longer-developing plays.',
    helpsAgainst: 'Aggressive secondaries and defenses that spin extra hats toward the box.',
    vulnerableTo: 'Two-high shells and pass rush units that collapse the pocket before routes open.',
    visuals: 'Longer stems, deeper launches, bigger air-yard swings, and boom-or-bust drive texture.',
  },
};

export const FRANCHISE_DEFENSE_SCHEME_PROFILES: Record<FranchiseDefenseScheme, FranchiseSchemeProfile> = {
  front: {
    identity: 'Box-first resistance',
    summary: 'Crowds the fit, wins early against the run, and dares offenses to survive tighter space and more bodies near the line of scrimmage.',
    strengths: ['Best at squeezing `power` and run-forward offenses', 'Creates tighter fit pictures and downhill support near the line'],
    weaknesses: ['`Spread` spacing and boots can punish the extra bodies in the box', 'Single-high structure gives `vertical` attacks cleaner isolated shots'],
    bestWith: 'Strong front seven, downhill linebackers, and safeties who can trigger with confidence.',
    helpsAgainst: 'Run-heavy teams and offenses that want patient body-blow drives.',
    vulnerableTo: 'Spread spacing, boots, and offenses that can punish heavier boxes.',
    visuals: 'Tighter box counts, faster second-level triggers, and more single-high pressure on the edges.',
  },
  shell: {
    identity: 'Explosive-play prevention',
    summary: 'Protects the roof, keeps the ball in front, and asks the offense to be patient enough to string together mistake-free drives.',
    strengths: ['Best answer to `vertical` teams and deep-shot hunting', 'Keeps the ball in front and lowers explosive damage over time'],
    weaknesses: ['`Power` and run-forward teams can lean on lighter boxes', '`West Coast` and balanced attacks can take rhythm throws underneath'],
    bestWith: 'Smart safeties, disciplined corners, and a defense comfortable rallying together.',
    helpsAgainst: 'Vertical passing teams and big-play hunters.',
    vulnerableTo: 'Efficient run games and rhythm offenses that are happy taking what is there.',
    visuals: 'Two-high spacing, deeper shells, softer pre-snap posture, and a more controlled feel.',
  },
  pressure: {
    identity: 'Havoc and disruption',
    summary: 'Attacks protections, speeds up the quarterback, and embraces chaos in exchange for sacks, hits, and forced mistakes.',
    strengths: ['Best against `vertical` and other slow-developing pass games', 'Creates sacks, hurried throws, and negative plays when it wins early'],
    weaknesses: ['`West Coast` quick game and `spread` screens can punish overcommitment', 'Missed tackles turn the chaos into explosive answers'],
    bestWith: 'Corners who can survive isolation and front players who finish when they win.',
    helpsAgainst: 'Vertical or slow-developing offenses that need clean pockets.',
    vulnerableTo: 'Quick game, screens, and offenses built to punish overcommitment.',
    visuals: 'Mugged fronts, harder triggers, faster pursuit angles, and a more chaotic snap cadence.',
  },
  match: {
    identity: 'Reactive and disciplined',
    summary: 'Balances leverage, pattern-matching, and smart support so the defense can answer a wide range of route structures without living on pure aggression.',
    strengths: ['Best against `west-coast`, `spread`, and layered intermediate route games', 'Keeps structure without giving away every easy underneath throw'],
    weaknesses: ['`Power` can stress it downhill if the box gets displaced', 'True vertical speed can still force hard leverage decisions'],
    bestWith: 'Intelligent second-level defenders and DBs who can pass routes off smoothly.',
    helpsAgainst: 'Spread spacing, intermediate timing games, and varied route combinations.',
    vulnerableTo: 'Elite power rushing or top-end speed that can still stress leverage rules.',
    visuals: 'Balanced shells, smoother handoffs in coverage, and disciplined pursuit instead of constant all-out attack.',
  },
};

export type FranchiseIdentity = {
  offense: FranchiseOffenseScheme;
  defense: FranchiseDefenseScheme;
  runBias: number;
  aggression: number;
  fourthDownAggression?: number;
  onsideKickAggression?: number;
  patAggression?: number;
  tempo?: number;
  offensiveFocus?: OffensiveFocus;
  defensiveAssignment?: DefensiveAssignment;
  personnelPreference?: PersonnelPreference;
  coachingPresets?: FranchiseCoachingPreset[];
};

export const OFFENSIVE_FOCUSES = ['balanced', 'wr1', 'wr2', 'slot', 'te', 'rb', 'spread'] as const;
export type OffensiveFocus = typeof OFFENSIVE_FOCUSES[number];

export const DEFENSIVE_ASSIGNMENTS = ['balanced', 'shadow-wr1', 'shadow-featured', 'bracket-featured', 'run-focus'] as const;
export type DefensiveAssignment = typeof DEFENSIVE_ASSIGNMENTS[number];

export const PERSONNEL_PREFERENCES = ['adaptive', 'spread', 'base', 'multiple-te', 'two-back'] as const;
export type PersonnelPreference = typeof PERSONNEL_PREFERENCES[number];

export const FRANCHISE_COACHING_PRESET_IDS = ['preset-1', 'preset-2', 'preset-3', 'preset-4'] as const;
export type FranchiseCoachingPresetId = typeof FRANCHISE_COACHING_PRESET_IDS[number];
export type FranchiseCoachingPreset = {
  id: FranchiseCoachingPresetId;
  name: string;
  revision: number;
  offense: FranchiseOffenseScheme;
  defense: FranchiseDefenseScheme;
  runBias: number;
  aggression: number;
  fourthDownAggression: number;
  onsideKickAggression: number;
  patAggression: number;
  offensiveFocus: OffensiveFocus;
  defensiveAssignment: DefensiveAssignment;
  personnelPreference: PersonnelPreference;
  depthChart?: FranchiseDepthChartConfig;
};

export type FranchiseNftBinding = {
  /** EVM contract address of the collection this franchise's NFT belongs to. */
  contractAddress: string;
  /** Token id within the collection. */
  tokenId: string;
  /**
   * EVM chain id in hex, e.g. '0x1' for Ethereum mainnet. Optional — omit it and
   * the binding is treated as Ethereum mainnet. Only set it for NFTs on another
   * chain (Base '0x2105', Polygon '0x89', etc.).
   */
  chainId?: string;
  /**
   * Content revision for the owner-managed team image. The API derives this
   * from the PNG bytes and persists it with CORE so every client can use the
   * immutable CDN object after a refresh.
   */
  imageVersion?: string;
};

export type FranchiseTeam = {
  /**
   * Canonical immutable franchise id. Hydration normalizes this to the team NFT
   * token id so existing `team.id` consumers automatically use the safe key.
   */
  id: string;
  /** Immutable token-backed franchise key. Hydration fills this for legacy data. */
  franchiseId?: string;
  city: string;
  name: string;
  abbr: string;
  colors: TeamColors;
  identity?: FranchiseIdentity;
  visuals?: FranchiseVisuals;
  /**
   * Optional binding to the on-chain NFT that represents ownership of this
   * franchise. Teams span multiple contracts, so the binding is self-describing
   * (contract + token + chain) rather than relying on a single shared contract.
   */
  nft?: FranchiseNftBinding;
};

/**
 * The franchise's identity-of-record — the stable fields that belong to no
 * overlay (branding owns colors/visuals, identity owns play-style, depth-chart
 * owns membership). Persisted as the `CORE` record in the franchise-data table;
 * the full `FranchiseTeam` is reconstructed by layering the branding + identity
 * records back on via `assembleFranchiseTeam`.
 */
export type FranchiseCore = Pick<FranchiseTeam, 'id' | 'franchiseId' | 'city' | 'name' | 'abbr' | 'nft'>;

export type FranchiseRosterFile = {
  players: FranchisePlayer[];
};

export type ColorShift = {
  hueShift?: number;
  chromaShift?: number;
  lightnessShift?: number;
};

export const FRANCHISE_FIELD_BACKGROUNDS = [
  {
    id: 'cyber',
    label: 'Cyber',
    assetPath: 'assets/textures/background/cyber-texture.png',
    opacity: 0.45,
    shadeAlpha: 0.32,
  },
  {
    id: 'sky',
    label: 'Sky',
    assetPath: 'assets/textures/background/sky-texture.png',
    opacity: 0.72,
    shadeAlpha: 0.2,
  },
  {
    id: 'mountain',
    label: 'Mountain',
    assetPath: 'assets/textures/background/mountain-texture.png',
    opacity: 0.68,
    shadeAlpha: 0.24,
  },
  {
    id: 'grass',
    label: 'Grass',
    assetPath: 'assets/textures/background/grass-texture.png',
    opacity: 0.5,
    shadeAlpha: 0.3,
  },
] as const;

export type FranchiseFieldBackgroundId = typeof FRANCHISE_FIELD_BACKGROUNDS[number]['id'];

export type FranchiseFieldBackgroundVisual = ColorShift & {
  texture?: FranchiseFieldBackgroundId;
};

export type FranchiseFieldLogoVisual = {
  src?: string;
  hueShift?: number;
  chromaShift?: number;
  lightnessShift?: number;
};

export type FranchisePlayerSpritePalette = {
  helmet?: string;
  jersey?: string;
  pants?: string;
  accent?: string;
  gloves?: string;
  shoes?: string;
  hair?: string;
};

export type FranchiseFieldVisuals = {
  homeFieldColor?: ColorShift;
  fieldMarkingColor?: ColorShift;
  fieldBoardColor?: ColorShift;
  fieldBackground?: FranchiseFieldBackgroundVisual;
  fieldGoalPostColor?: ColorShift;
  fieldGoalBaseColor?: ColorShift;
  logo?: string | FranchiseFieldLogoVisual;
};

export const FRANCHISE_POSSESSION_SONGS = [
  {
    id: 'fight-song-1',
    label: 'Fight Song 1',
    src: 'https://assets.dflplaymaker.com/assets/sounds/fightSong.mp3?v=match-loop-v4',
  },
  {
    id: 'fight-song-2',
    label: 'Fight Song 2',
    src: 'https://assets.dflplaymaker.com/assets/sounds/fightSong2.mp3?v=match-loop-v5',
  },
] as const;

export type FranchisePossessionSongId = typeof FRANCHISE_POSSESSION_SONGS[number]['id'];

export type FranchiseMusicVisuals = {
  possessionSong?: FranchisePossessionSongId;
};

export type FranchiseVisuals = {
  field?: FranchiseFieldVisuals;
  music?: FranchiseMusicVisuals;
  playerSprite?: FranchisePlayerSpritePalette;
  uniforms?: Record<string, Record<string, string>>;
  playerRoles?: Record<string, Record<string, string>>;
  [key: string]: unknown;
};

export type FranchiseBranding = {
  colors: TeamColors;
  visuals: FranchiseVisuals & {
    field: {
      homeFieldColor: Required<ColorShift>;
      fieldMarkingColor: Required<ColorShift>;
      fieldBoardColor: Required<ColorShift>;
      fieldBackground: Required<FranchiseFieldBackgroundVisual>;
      fieldGoalPostColor: Required<ColorShift>;
      fieldGoalBaseColor: Required<ColorShift>;
      logo: Required<FranchiseFieldLogoVisual>;
    };
    music: Required<FranchiseMusicVisuals>;
    playerSprite: Required<FranchisePlayerSpritePalette>;
  };
};

export type FranchiseBrandingInput = {
  colors?: Partial<TeamColors>;
  visuals?: FranchiseVisuals;
};

export type FranchiseBrandingEntry = {
  franchiseId: string;
  branding: FranchiseBranding;
  hasOverride: boolean;
  updatedAt: string | null;
};

export type FranchiseIdentityInput = Partial<Pick<
  FranchiseIdentity,
  'offense' | 'defense' | 'runBias' | 'aggression' | 'fourthDownAggression' | 'onsideKickAggression' | 'patAggression'
  | 'offensiveFocus' | 'defensiveAssignment' | 'personnelPreference'
  | 'coachingPresets'
>>;

export type FranchiseIdentityEntry = {
  franchiseId: string;
  identity: FranchiseIdentity;
  hasOverride: boolean;
  updatedAt: string | null;
};

export type FranchisesData = {
  teams: FranchiseTeam[];
};

export const playbookData = playbookJson;
export const rulesData = rulesJson;

// ---------------------------------------------------------------------------
// Hydrated data snapshot (franchises + players)
//
// Franchises and the player pool used to be bundled JSON imported here. They now
// live in permanent storage (DynamoDB in prod, JSON files in local dev) and are
// loaded once at process/app startup via `hydrateSharedData`. The accessors below
// stay synchronous so the simulator's purity contract holds — the async load
// happens *before* any `simulateGame()` call, never inside one.
//
// `franchiseTeams` is an exported `let` reassigned on hydrate; ES module live
// bindings mean consumers that read it inside functions/render see the populated
// value. Do not read it at module-evaluation time.
// ---------------------------------------------------------------------------

export type SharedDataSnapshot = {
  franchises: FranchiseTeam[];
  players: FranchisePlayer[];
};

export let franchiseTeams: FranchiseTeam[] = [];
let playersById = new Map<string, FranchisePlayer>();
let allPlayers: FranchisePlayer[] = [];
let sharedDataHydrated = false;

/**
 * Populate the franchise + player snapshot. Call once during startup before any
 * code reads franchises or players. Idempotent — the last call wins.
 */
export function hydrateSharedData(snapshot: SharedDataSnapshot): void {
  franchiseTeams = snapshot.franchises.map((team) => {
    const franchiseId = getFranchiseId(team);
    return applySeasonThreeFranchisePreset({
      ...team,
      id: franchiseId,
      franchiseId,
    });
  });
  allPlayers = snapshot.players.map((player) => normalizeFranchisePlayer(player));
  playersById = new Map(allPlayers.map((player) => [player.id, player]));
  sharedDataHydrated = true;
}

/** True once `hydrateSharedData` has run. */
export function isSharedDataHydrated(): boolean {
  return sharedDataHydrated;
}

function ensureSharedDataHydrated(): void {
  if (!sharedDataHydrated) {
    throw new Error(
      'Shared data is not hydrated. Call hydrateSharedData({ franchises, players }) '
      + 'during startup before accessing franchises or players.',
    );
  }
}

export const FRANCHISE_POSITION_ORDER = FRANCHISE_PLAYER_POSITIONS;

export const FRANCHISE_DEPTH_CHART_SECTIONS = [
  { id: 'offense', label: 'Offense', positions: ['QB', 'RB', 'WR', 'TE', 'OT', 'OG', 'C'] },
  { id: 'defense', label: 'Defense', positions: ['DE', 'DT', 'LB', 'CB', 'S'] },
  { id: 'special-teams', label: 'Special Teams', positions: ['K', 'P'] },
] as const;

export const DEFAULT_COLOR_SHIFT: Required<ColorShift> = {
  hueShift: 0,
  chromaShift: 0,
  lightnessShift: 0,
};

export const DEFAULT_FIELD_BACKGROUND: Required<FranchiseFieldBackgroundVisual> = {
  texture: 'cyber',
  ...DEFAULT_COLOR_SHIFT,
};

export const DEFAULT_FIELD_LOGO: Required<FranchiseFieldLogoVisual> = {
  src: 'assets/logos/cyber-midfield.svg',
  hueShift: 0,
  chromaShift: 0,
  lightnessShift: 0,
};

export const DEFAULT_FRANCHISE_MUSIC: Required<FranchiseMusicVisuals> = {
  possessionSong: 'fight-song-1',
};

export const DEFAULT_FRANCHISE_IDENTITY: Required<Pick<
  FranchiseIdentity,
  'offense' | 'defense' | 'runBias' | 'aggression' | 'fourthDownAggression' | 'onsideKickAggression' | 'patAggression'
  | 'offensiveFocus' | 'defensiveAssignment' | 'personnelPreference'
>> = {
  offense: 'balanced',
  defense: 'front',
  runBias: 0.5,
  aggression: 0.5,
  fourthDownAggression: 0.5,
  onsideKickAggression: 0.5,
  patAggression: 0.5,
  offensiveFocus: 'balanced',
  defensiveAssignment: 'balanced',
  personnelPreference: 'adaptive',
};

export function getDefaultPlayerSpritePalette(colors: TeamColors): Required<FranchisePlayerSpritePalette> {
  return {
    helmet: colors.primary,
    jersey: colors.primary,
    pants: colors.secondary,
    accent: colors.accent,
    gloves: colors.accent,
    shoes: '#111111',
    hair: '#2c1a12',
  };
}

export function getFranchiseById(id: string): FranchiseTeam | undefined {
  ensureSharedDataHydrated();
  const resolvedId = canonicalTeamId(id);
  const team = franchiseTeams.find((entry) => getFranchiseId(entry) === resolvedId);
  if (!team) {
    return undefined;
  }

  return {
    ...team,
    identity: normalizeFranchiseIdentity(team.identity),
  };
}

export function getAllFranchises(): FranchiseTeam[] {
  ensureSharedDataHydrated();
  return franchiseTeams.map((team) => ({
    ...team,
    identity: normalizeFranchiseIdentity(team.identity),
  }));
}

/**
 * Neutral palette used only when a `CORE` franchise has no branding record yet
 * (e.g. a franchise just created via `PUT /api/franchises` before its colors are
 * set through the branding route). Real franchises always carry a branding
 * record, so this is a resilience fallback, not a normal code path.
 */
export const DEFAULT_FRANCHISE_CORE_COLORS: TeamColors = {
  primary: '#6b7280',
  secondary: '#374151',
  accent: '#e5e7eb',
};

/**
 * Reconstruct a full `FranchiseTeam` from its decomposed franchise-data records:
 * the `CORE` identity-of-record plus the `BRANDING` (colors/visuals) and
 * `IDENTITY` (play-style) overlays. Branding owns colors, so an absent branding
 * record falls back to {@link DEFAULT_FRANCHISE_CORE_COLORS} rather than throwing,
 * keeping hydration resilient. The identity is left raw here — callers route it
 * through `normalizeFranchiseIdentity` (as `getFranchiseById` already does).
 */
export function assembleFranchiseTeam(
  core: FranchiseCore,
  branding?: FranchiseBranding,
  identity?: FranchiseIdentity,
): FranchiseTeam {
  const franchiseId = getFranchiseId(core);
  return {
    id: franchiseId,
    franchiseId,
    city: core.city,
    name: core.name,
    abbr: core.abbr,
    ...(core.nft !== undefined ? { nft: core.nft } : {}),
    colors: branding?.colors ?? DEFAULT_FRANCHISE_CORE_COLORS,
    ...(branding?.visuals !== undefined ? { visuals: branding.visuals } : {}),
    ...(identity !== undefined ? { identity } : {}),
  };
}

export function getFranchiseDisplayName(team: FranchiseTeam): string {
  return `${team.city} ${team.name}`;
}

function cloneFranchisePlayer(player: FranchisePlayer): FranchisePlayer {
  return normalizeFranchisePlayer({
    ...player,
    ratings: { ...player.ratings },
    appearance: player.appearance ? { ...player.appearance } : undefined,
  });
}

export function getPlayerById(playerId: string): FranchisePlayer | null {
  ensureSharedDataHydrated();
  const player = playersById.get(canonicalPlayerId(playerId));
  return player ? cloneFranchisePlayer(player) : null;
}

/** The full player attribute pool (independent of any team's depth chart). */
export function getAllPlayers(): FranchisePlayer[] {
  ensureSharedDataHydrated();
  return allPlayers.map(cloneFranchisePlayer);
}

/**
 * Resolve a depth-chart config into a roster of full player objects. Membership
 * lives entirely in the depth-chart table now, so callers load the config (from
 * the API, the table file, or local state) and resolve it here. A player's
 * position is the group they appear in; unknown ids are dropped.
 */
export function resolveDepthChartRoster(
  config: FranchiseDepthChartConfig | null | undefined,
): FranchisePlayer[] {
  if (!config?.positions) {
    return [];
  }

  return FRANCHISE_PLAYER_POSITIONS.flatMap((position) =>
    (config.positions[position] || [])
      .map((playerId) => {
        const player = getPlayerById(playerId);
        return player ? normalizeFranchisePlayer({
          ...player,
          position,
          playStyle: config.playerStyles?.[playerId],
        }) : null;
      })
      .filter((player): player is FranchisePlayer => player !== null),
  );
}

function normalizeColorShift(shift?: ColorShift | null): Required<ColorShift> {
  return {
    ...DEFAULT_COLOR_SHIFT,
    ...(shift || {}),
  };
}

function normalizeFieldBackground(
  background?: FranchiseFieldBackgroundVisual | null,
): Required<FranchiseFieldBackgroundVisual> {
  const requestedTexture = background?.texture;
  const texture = FRANCHISE_FIELD_BACKGROUNDS.some((option) => option.id === requestedTexture)
    ? requestedTexture as FranchiseFieldBackgroundId
    : DEFAULT_FIELD_BACKGROUND.texture;

  return {
    ...DEFAULT_FIELD_BACKGROUND,
    ...(background || {}),
    texture,
  };
}

function normalizeFieldLogo(logo?: string | FranchiseFieldLogoVisual | null): Required<FranchiseFieldLogoVisual> {
  if (typeof logo === 'string') {
    return {
      ...DEFAULT_FIELD_LOGO,
      src: logo,
    };
  }

  return {
    ...DEFAULT_FIELD_LOGO,
    ...(logo || {}),
  };
}

function normalizePlayerSpritePalette(
  palette: FranchisePlayerSpritePalette | null | undefined,
  colors: TeamColors,
): Required<FranchisePlayerSpritePalette> {
  return {
    ...getDefaultPlayerSpritePalette(colors),
    ...(palette || {}),
  };
}

function normalizeFranchiseMusic(music?: FranchiseMusicVisuals | null): Required<FranchiseMusicVisuals> {
  const possessionSong = music?.possessionSong;
  const validSong = FRANCHISE_POSSESSION_SONGS.find((song) => song.id === possessionSong);
  if (validSong) {
    return { possessionSong: validSong.id };
  }

  return {
    possessionSong: DEFAULT_FRANCHISE_MUSIC.possessionSong,
  };
}

function clampSliderValue(value: number, fallback: number): number {
  if (!Number.isFinite(value)) {
    return fallback;
  }

  return Math.max(0, Math.min(1, Math.round(value * 100) / 100));
}

export function normalizeFranchiseOffenseScheme(
  value: unknown,
  fallback: FranchiseOffenseScheme = DEFAULT_FRANCHISE_IDENTITY.offense,
): FranchiseOffenseScheme {
  if (typeof value !== 'string') {
    return fallback;
  }

  if ((FRANCHISE_OFFENSE_SCHEMES as readonly string[]).includes(value)) {
    return value as FranchiseOffenseScheme;
  }

  return LEGACY_OFFENSE_SCHEME_ALIASES[value as keyof typeof LEGACY_OFFENSE_SCHEME_ALIASES] || fallback;
}

export function normalizeFranchiseDefenseScheme(
  value: unknown,
  fallback: FranchiseDefenseScheme = DEFAULT_FRANCHISE_IDENTITY.defense,
): FranchiseDefenseScheme {
  if (typeof value !== 'string') {
    return fallback;
  }

  if ((FRANCHISE_DEFENSE_SCHEMES as readonly string[]).includes(value)) {
    return value as FranchiseDefenseScheme;
  }

  return LEGACY_DEFENSE_SCHEME_ALIASES[value as keyof typeof LEGACY_DEFENSE_SCHEME_ALIASES] || fallback;
}

function normalizeFranchiseIdentity(identity?: FranchiseIdentity | null): FranchiseIdentity {
  return {
    offense: normalizeFranchiseOffenseScheme(identity?.offense, DEFAULT_FRANCHISE_IDENTITY.offense),
    defense: normalizeFranchiseDefenseScheme(identity?.defense, DEFAULT_FRANCHISE_IDENTITY.defense),
    runBias: clampSliderValue(identity?.runBias ?? DEFAULT_FRANCHISE_IDENTITY.runBias, DEFAULT_FRANCHISE_IDENTITY.runBias),
    aggression: clampSliderValue(identity?.aggression ?? DEFAULT_FRANCHISE_IDENTITY.aggression, DEFAULT_FRANCHISE_IDENTITY.aggression),
    fourthDownAggression: clampSliderValue(
      identity?.fourthDownAggression ?? DEFAULT_FRANCHISE_IDENTITY.fourthDownAggression,
      DEFAULT_FRANCHISE_IDENTITY.fourthDownAggression,
    ),
    onsideKickAggression: clampSliderValue(
      identity?.onsideKickAggression ?? DEFAULT_FRANCHISE_IDENTITY.onsideKickAggression,
      DEFAULT_FRANCHISE_IDENTITY.onsideKickAggression,
    ),
    patAggression: clampSliderValue(
      identity?.patAggression ?? DEFAULT_FRANCHISE_IDENTITY.patAggression,
      DEFAULT_FRANCHISE_IDENTITY.patAggression,
    ),
    offensiveFocus: OFFENSIVE_FOCUSES.includes(identity?.offensiveFocus as OffensiveFocus)
      ? identity!.offensiveFocus
      : DEFAULT_FRANCHISE_IDENTITY.offensiveFocus,
    defensiveAssignment: DEFENSIVE_ASSIGNMENTS.includes(identity?.defensiveAssignment as DefensiveAssignment)
      ? identity!.defensiveAssignment
      : DEFAULT_FRANCHISE_IDENTITY.defensiveAssignment,
    personnelPreference: PERSONNEL_PREFERENCES.includes(identity?.personnelPreference as PersonnelPreference)
      ? identity!.personnelPreference
      : DEFAULT_FRANCHISE_IDENTITY.personnelPreference,
    ...(typeof identity?.tempo === 'number' && Number.isFinite(identity.tempo)
      ? { tempo: identity.tempo }
      : {}),
  };
}

export function getDefaultFranchiseBranding(team: FranchiseTeam): FranchiseBranding {
  const visuals = (team.visuals || {}) as FranchiseVisuals;
  const field = visuals.field || {};

  return {
    colors: {
      primary: team.colors.primary,
      secondary: team.colors.secondary,
      accent: team.colors.accent,
    },
    visuals: {
      ...visuals,
      field: {
        homeFieldColor: normalizeColorShift(field.homeFieldColor),
        fieldMarkingColor: normalizeColorShift(field.fieldMarkingColor),
        fieldBoardColor: normalizeColorShift(field.fieldBoardColor),
        fieldBackground: normalizeFieldBackground(field.fieldBackground),
        fieldGoalPostColor: normalizeColorShift(field.fieldGoalPostColor),
        fieldGoalBaseColor: normalizeColorShift(field.fieldGoalBaseColor),
        logo: normalizeFieldLogo(field.logo),
      },
      music: normalizeFranchiseMusic(visuals.music),
      playerSprite: normalizePlayerSpritePalette(visuals.playerSprite, team.colors),
    },
  };
}

export function getDefaultFranchiseIdentity(team: FranchiseTeam): FranchiseIdentity {
  return normalizeFranchiseIdentity(team.identity);
}

export function mergeFranchiseIdentity(
  team: FranchiseTeam,
  input?: FranchiseIdentityInput | null,
): FranchiseIdentity {
  const defaults = getDefaultFranchiseIdentity(team);

  return {
    ...defaults,
    offense: normalizeFranchiseOffenseScheme(input?.offense, defaults.offense),
    defense: normalizeFranchiseDefenseScheme(input?.defense, defaults.defense),
    runBias: clampSliderValue(input?.runBias ?? defaults.runBias, defaults.runBias),
    aggression: clampSliderValue(input?.aggression ?? defaults.aggression, defaults.aggression),
    fourthDownAggression: clampSliderValue(
      input?.fourthDownAggression ?? defaults.fourthDownAggression ?? DEFAULT_FRANCHISE_IDENTITY.fourthDownAggression,
      defaults.fourthDownAggression ?? DEFAULT_FRANCHISE_IDENTITY.fourthDownAggression,
    ),
    onsideKickAggression: clampSliderValue(
      input?.onsideKickAggression ?? defaults.onsideKickAggression ?? DEFAULT_FRANCHISE_IDENTITY.onsideKickAggression,
      defaults.onsideKickAggression ?? DEFAULT_FRANCHISE_IDENTITY.onsideKickAggression,
    ),
    patAggression: clampSliderValue(
      input?.patAggression ?? defaults.patAggression ?? DEFAULT_FRANCHISE_IDENTITY.patAggression,
      defaults.patAggression ?? DEFAULT_FRANCHISE_IDENTITY.patAggression,
    ),
    offensiveFocus: OFFENSIVE_FOCUSES.includes(input?.offensiveFocus as OffensiveFocus)
      ? input!.offensiveFocus
      : defaults.offensiveFocus,
    defensiveAssignment: DEFENSIVE_ASSIGNMENTS.includes(input?.defensiveAssignment as DefensiveAssignment)
      ? input!.defensiveAssignment
      : defaults.defensiveAssignment,
    personnelPreference: PERSONNEL_PREFERENCES.includes(input?.personnelPreference as PersonnelPreference)
      ? input!.personnelPreference
      : defaults.personnelPreference,
    coachingPresets: input?.coachingPresets || defaults.coachingPresets,
  };
}

export function mergeFranchiseBranding(
  team: FranchiseTeam,
  input?: FranchiseBrandingInput | null,
): FranchiseBranding {
  const defaults = getDefaultFranchiseBranding(team);
  const inputVisuals = (input?.visuals || {}) as FranchiseVisuals;
  const inputField = inputVisuals.field || {};
  const inputMusic = inputVisuals.music || defaults.visuals.music;
  const mergedColors = {
    ...defaults.colors,
    ...(input?.colors || {}),
  };
  const {
    field: _ignoredField,
    music: _ignoredMusic,
    playerSprite: _ignoredPlayerSprite,
    ...restInputVisuals
  } = inputVisuals;
  const hasPlayerSpriteOverride = Boolean(inputVisuals.playerSprite);
  const playerSprite = hasPlayerSpriteOverride
    ? normalizePlayerSpritePalette(inputVisuals.playerSprite, mergedColors)
    : input?.colors
      ? getDefaultPlayerSpritePalette(mergedColors)
      : defaults.visuals.playerSprite;

  return {
    colors: mergedColors,
    visuals: {
      ...defaults.visuals,
      ...restInputVisuals,
      field: {
        ...defaults.visuals.field,
        homeFieldColor: normalizeColorShift(inputField.homeFieldColor || defaults.visuals.field.homeFieldColor),
        fieldMarkingColor: normalizeColorShift(inputField.fieldMarkingColor || defaults.visuals.field.fieldMarkingColor),
        fieldBoardColor: normalizeColorShift(inputField.fieldBoardColor || defaults.visuals.field.fieldBoardColor),
        fieldBackground: normalizeFieldBackground(inputField.fieldBackground || defaults.visuals.field.fieldBackground),
        fieldGoalPostColor: normalizeColorShift(inputField.fieldGoalPostColor || defaults.visuals.field.fieldGoalPostColor),
        fieldGoalBaseColor: normalizeColorShift(inputField.fieldGoalBaseColor || defaults.visuals.field.fieldGoalBaseColor),
        logo: normalizeFieldLogo(inputField.logo || defaults.visuals.field.logo),
      },
      music: normalizeFranchiseMusic(inputMusic),
      playerSprite,
    },
  };
}

export function applyBrandingToTeam<
  TTeam extends { colors: TeamColors; visuals?: Record<string, unknown> },
>(
  team: TTeam,
  input?: FranchiseBrandingInput | null,
): TTeam {
  const branding = mergeFranchiseBranding(team as unknown as FranchiseTeam, input);
  return {
    ...team,
    colors: branding.colors,
    visuals: branding.visuals,
  };
}

export function applyIdentityToTeam<
  TTeam extends { identity?: FranchiseIdentity },
>(
  team: TTeam,
  input?: FranchiseIdentityInput | null,
): TTeam {
  const identity = mergeFranchiseIdentity(team as unknown as FranchiseTeam, input);
  return {
    ...team,
    identity,
  };
}
